/**
 * `Context_Registry.ts` — port of `AI/Registry/Context_Registry.cs`.
 *
 * Web slice: `Prompt_SceneContextAgent` (hierarchy helper agent over the
 * Responses API) is deferred; `Get_Scene_Context` runs local string matching
 * only. `Get_Part_Context` DOES consult the knowledge agent for documents
 * (see `AIContextEngineModel.KnowledgeTransport.ts`); the Knowledge tool
 * family itself lives in `Docs_Registry.ts`.
 *
 * Unity registers the context tools with `Enabled = false` as the
 * pre-refresh default; `refreshToolAvailability(hasProject)` flips them on
 * once a project is open. Mirrored here.
 */

import { AgentToolNames } from "./agentToolNames";
import {
  type AIAgentTool,
  buildParams,
  stringList,
  stringParam,
} from "./AIAgentToolRegistryModel";

export function addContextTools(tools: AIAgentTool[]): void {
  tools.push(
    {
      name: AgentToolNames.GetProjectContext,
      category: "Context",
      description:
        "Returns project-level info: project name, scenes (with notes), models per scene, and the currently active scene. A model entry may carry an animation indicator, e.g. '(hasAnimation:true)'; the animation's names are listed by Get_Scene_Context. Always call fresh - project context can change at any time.",
      humanDescription:
        "View project details, scene names, notes, models, and the current scene.",
      parameters: buildParams(),
      enabled: false,
      writerOnly: false,
    },
    {
      name: AgentToolNames.GetSceneContext,
      category: "Context",
      labelWhenActive: "Looking through scene...",
      description:
        "Looks up the current scene structure (parts, top-level models/root paths, selection sets, text boxes). " +
        "With SearchQuery, the helper tries fast local string matching against the hierarchy and returns the relevant lines. " +
        "With neither SearchQuery nor prompt, returns the full scene context. " +
        "Use this primarily to locate/disambiguate parts or models, including generic references like 'the model', or resolve paths for downstream tools. " +
        "When the user names a part, top-level model, selection set, or text box, call this to resolve that explicit name even if User Context currently points at, last pointed at, or selected a matching object. " +
        "Selecting a model is not required before model-level operations; identify the target root model with this tool instead. " +
        "Response line formats are documented in the system prompt under 'Hierarchy Path Format'. " +
        "If a target name matches a selection set name, the selection-set line is returned and should be used directly. " +
        "If one relevant match is returned for a named target, treat it as resolved; copy the path verbatim into downstream tool args.",
      humanDescription:
        "Find matching parts, top-level models, selection sets, or text boxes in the scene hierarchy, or return the full hierarchy when needed.",
      parameters: buildParams({
        SearchQuery: stringList(
          "Raw search terms for fast local hierarchy matching, e.g. ['Part657'], ['Assembly A'], ['Label B'].",
        ),
        prompt: stringParam(
          "Short request for the subset you want, e.g. 'Return objects identified as <what the user named>'. Do NOT specify response fields/formatting. Do NOT include the purpose ('for move_parts', 'to hide').",
        ),
      }),
      enabled: false,
      writerOnly: false,
    },
    {
      name: AgentToolNames.GetPartContext,
      category: "Context",
      labelWhenActive: "Searching part context...",
      description:
        "Returns part context for a specific part: saved notes/descriptions for all hierarchy levels, plus relevant findings from project documents. Primary tool for answering part questions without sending the full scene hierarchy. Provide partPath. The prompt should include the user's question.",
      humanDescription:
        "Get notes and document findings for a specific part to answer part questions.",
      parameters: buildParams({
        prompt: stringParam(
          "The user question or request to answer about this part.",
          false,
        ),
        partPath: stringParam("Hierarchy path from Get_Scene_Context (e.g. '0,1,2').", false),
      }),
      enabled: false,
      writerOnly: false,
    },
    {
      name: AgentToolNames.TakeScreenshot,
      category: "Context",
      description: `Captures the user's current Campfire view and sends the image to the AI agent. Use to see what the user is looking at or which UI elements are visible (e.g. 'what am I pointing at?', 'what is this?', 'what do you see?'). For AI inspection only - does NOT create scene content.`,
      humanDescription: "Capture the user's current view for AI inspection only.",
      parameters: buildParams(),
      enabled: true,
      writerOnly: false,
    },
  );
}
